import posthog from 'posthog-js'

const posthogKey = process.env.REACT_APP_POSTHOG_KEY
const posthogHost = process.env.REACT_APP_POSTHOG_HOST

// ── Anonymous dev-mode toggle (per-browser-profile) ──────────────────────
// Visiting the deployed app with ?dev=1 sets a persistent localStorage flag
// so a developer can browse anonymously (not signed in — no identity to
// match against) without PostHog recording it. ?dev=0 clears the flag,
// restoring normal tracking on that browser. This read/write runs once, at
// module load, before the isLocalDev/init decision below — this file is a
// module-level singleton imported once by src/index.js before App renders,
// so there is no race and no re-run per page load.
// Deliberately localStorage, not a cookie: an incognito/private window has
// no access to this storage, so it always starts clean regardless of the
// toggle's state on the regular profile — that's expected/desired, not a
// bug.
const DEV_MODE_STORAGE_KEY = 'hs_posthog_devmode'

if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
  const devParam = new URLSearchParams(window.location.search).get('dev')
  if (devParam === '1') {
    window.localStorage.setItem(DEV_MODE_STORAGE_KEY, 'true')
  } else if (devParam === '0') {
    window.localStorage.removeItem(DEV_MODE_STORAGE_KEY)
  }
}

const isDevModeFlagged =
  typeof window !== 'undefined' &&
  typeof window.localStorage !== 'undefined' &&
  window.localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true'

// ── Localhost skip, with explicit opt-in override ────────────────────────
// No env var set → always skip PostHog on localhost, no exceptions, no
// trap. Set REACT_APP_POSTHOG_FORCE_LOCAL=true (e.g. in .env.local) only
// when you deliberately want to verify a new event fires correctly during
// local development — this is the one explicit opt-in to force init() to
// run on localhost.
const isLocalDev =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
  process.env.REACT_APP_POSTHOG_FORCE_LOCAL !== 'true'

// ── Internal test email match (independent of the above) ─────────────────
// Used by AuthContext.js's updateAuthenticatedUser to call
// posthog.opt_out_capturing() instead of identify() for a match, so John's
// own production test accounts never get identified at all.
//
// isLocalDev, isDevModeFlagged, and isInternalTestEmail() are three
// independent checks that do not layer or supersede each other — each is
// separately sufficient to stop capturing, and none depends on the others'
// state. In particular, if a jtav04*@gmail.com sign-in happens during a
// ?dev=1-flagged (or not) session, the email-based opt-out fires on its own
// regardless of the anon toggle's state.
export function isInternalTestEmail(email) {
  if (!email) return false
  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return false
  return domain.toLowerCase() === 'gmail.com' && localPart.toLowerCase().startsWith('jtav04')
}

if (!posthogKey || !posthogHost) {
  if (process.env.NODE_ENV === 'development') {
    const missingVariable = !posthogKey ? 'REACT_APP_POSTHOG_KEY' : 'REACT_APP_POSTHOG_HOST'
    throw new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`
    )
  }
} else if (isLocalDev || isDevModeFlagged) {
  // Local dev traffic and dev-mode-flagged anonymous browsing are both
  // noise, not signal — skip init entirely rather than filtering it out
  // downstream. No key required, no events sent.
  if (process.env.NODE_ENV === 'development') {
    console.info('[posthog] Skipping init on localhost')
  }
} else {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    // Autocapture turns every click into a raw $autocapture event with no
    // semantic meaning — a single triage completion alone generates ~40 of
    // them. We track intent explicitly instead (triage_started,
    // triage_completed, etc.), so autocapture is off.
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    // Must mirror the landing site's index.html snippet exactly — this is
    // what lets a visitor's identity carry from holasam.app into
    // portal.holasam.app as one continuous journey.
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // Session replay bills separately from events (5,000 recordings/month
    // free tier) — leaving this on is fine at current volume, but forms
    // here collect income figures, filing status, dependents, etc., so
    // mask all inputs by default rather than relying on PostHog's default
    // (which only masks password fields).
    session_recording: {
      maskAllInputs: true,
    },
  })
}

export default posthog